/*
  •	Copyright (c) 2023 Pegasystems Inc.
  •	All rights reserved.
*/
import ContextProcessor from '../context/context-processor';
import { dispatch } from '../store/state/state-manager';
import { SET_PROPERTY, FIELD_CHANGE } from '../store/state/actions';
import { clearMessages } from '../messages';
import Constants, { publicConstants } from '../constants';
import type { C11nEnv } from '../interpreter/c11n-env';
import type { ActionItem, ActionObject, BrowserEvent } from '../globals';
import EnvironmentInfo from '../environment-info';

import type { ActionHandlers } from './types';
import actionsHandler from './handler';
import { ActionManagerInstance } from './manager';

type ActionConfig = {
  parallel?: string;
  [key: string]: unknown;
};

type EventWithPossibleValue = {
  value?: unknown;
  prop?: unknown;
};

type EventWithPossibleAuxTarget = {
  auxiliaryTarget?: HTMLElement & {
    value: unknown;
    validationValue?: unknown;
    clearMessages?: boolean;
  };
};

class Processor {
  static #actionsQueue: ActionItem[] = [];
  static #paused = false;
  /**
   * [getActionHandlers]
   * Description : Returns the array of action handlers for all actions authored for a behaviour.
   * "behaviours": [
   *    {
   *      "behaviour": "onChange",
   *      "actions": [
   *        {
   *          "action": "refresh"
   *        }
   *      ]
   *    }
   * ]
   * @param actions     Array of actions.
   * @param behaviour   Behaviour for which actions are authored. Eg onChange is a behaviour for which "refresh" action can be authored.
   * @param eventType   Event type
   * @returns Array of action handlers.
   */
  static getActionHandlers = (
    actions: { action: string | number; config: ActionConfig }[],
    behaviour: string,
    eventType: string
  ): ActionHandlers => {
    const handlers: ActionHandlers = [];
    // Map the native events to pega behaviours
    const NATIVE_TO_PEGA_BEHAVIOUR_MAP = {
      mouseenter: 'hover',
      change: 'onChange',
      blur: 'onBlur'
    };
    // If the native to behavior mapping isn't available do not get the handlers.
    if (NATIVE_TO_PEGA_BEHAVIOUR_MAP[eventType as keyof typeof NATIVE_TO_PEGA_BEHAVIOUR_MAP] !== behaviour) {
      return handlers;
    }
    // For each of the action, get the handler push it into an array to be returned later.
    actions.forEach((actionObj) => {
      const handler = actionsHandler.getHandler(actionObj.action);
      const { config } = actionObj;
      handlers.push({
        handler,
        config
      });
    });
    return handlers;
  };

  /**
   * Checks whether given element is editable or
   * not.
   *
   * @param target target element
   * @returns boolean
   */
  static isEditable = (target: HTMLElement): boolean => {
    const { tagName } = target;
    return tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT' || tagName === 'DATETIME';
  };

  /**
   * checks whether given element supports end user input values
   * @param target target element
   * @returns boolean
   */
  static isNonKeyInput = (target: { tagName: string; type: string }): boolean => {
    const { tagName, type } = target;
    return tagName === 'SELECT' || type === 'checkbox' || type === 'radio';
  };

  /**
   * [createQueue]
   * Description : Creates the queue of actions to be executed. Queue contains action objects.
   * @param actionHandlers    List of action handlers
   * @param component         c11nEnv object
   * @param eventType         Native event type
   */
  static #createQueue = (actionHandlers: ActionHandlers, component: C11nEnv, eventType: string) => {
    for (let i = 0; i < actionHandlers.length; i += 1) {
      const actionHandler = actionHandlers[i];
      // For each of the action handlers, create an action object and push it into the queue.
      const entry = {
        actionHandler: actionHandler.handler,
        component,
        eventType,
        config: actionHandler.config
      };
      this.#actionsQueue.push(entry);
    }
  };

  /**
   *
   * Abstract function to handle the actions performed
   * in the view.
   * @param config contains the action config.
   * @param c11nEnv c11n_env object.
   */
  static buildActionObject = (config: object, c11nEnv: C11nEnv): ActionObject => {
    const actionObject: ActionObject = { ...config };
    actionObject.context = EnvironmentInfo.getRuntime() === 'Browser' ? c11nEnv.getContextName() : actionObject.context;
    actionObject.pageReference = c11nEnv.getPageReference();

    // For each user session, every object will get a unique actionMgrID
    //  to allow the ActionManager to track if/when it succeeds or fails
    //  See actions/manager.js
    actionObject.actionMgrID = ActionManagerInstance.getNextActionMgrId();

    return actionObject;
  };

  /**
   * [#processAction]
   * Description : For each of the actions in the queue, fires the handler.
   * @param actionItem    actionobject.
   *                  actionItem.actionHandler - Handler for the action
   *                  actionItem.component     - c11nEnv object
   *                  actionItem.config        - Action config
   *                  actionItem.event         - Native event type
   */
  static #processAction = (actionItem: ActionItem) => {
    const handler = actionItem.actionHandler;
    const c11nEnv = actionItem.component;
    const { config } = actionItem;
    const newConfig = {};
    ContextProcessor.resolveProps(config, newConfig, c11nEnv);
    if (handler) {
      handler(actionItem.eventType, newConfig, c11nEnv);

      this.#executeNextAction();
    }
  };

  /**
   * execute next action
   */
  static #executeNextAction = () => {
    if (!this.#paused) {
      if (this.#actionsQueue.length === 0) {
        return;
      }
      const actionItem = this.#actionsQueue.shift() as ActionItem;
      this.#processAction(actionItem);
      if (actionItem.config && actionItem.config.parallel === 'true' && this.#actionsQueue.length > 0) {
        this.#executeNextAction();
      }
    }
  };

  /**
   * processes the queue items
   */
  static #processQueue = () => {
    this.#executeNextAction();
  };

  /**
   * helps to execute functions
   *
   * @param actionHandlers handler function
   * @param component c11n_env object
   * @param eventType DOM Event type
   */
  static #executeActions = (actionHandlers: ActionHandlers, component: C11nEnv, eventType: string) => {
    this.#createQueue(actionHandlers, component, eventType);
    this.#processQueue();
  };

  /**
   * Returns the full reference
   *
   * @param component c11n_env object
   * @returns full path.
   */
  static getFullRef = (component: C11nEnv): string => {
    const pageReference = component.getPageReference();
    const prop = component.getComponentConfig().value;
    return ContextProcessor.getContextPath(pageReference, prop);
  };

  /**
   * [handleBehaviours]
   * Description : Gets the authored behaviours from the component config, filters the actions from it,
   * and excutes the corresponding handlers.
   * "behaviours": [
   *    {
   *      "behaviour": "onChange",
   *      "actions": [
   *        {
   *          "action": "refresh"
   *        }
   *      ]
   *    }
   * ]
   * @param component   c11nEnv
   * @param eventType   Native event type
   */
  static #handleBehaviours(component: C11nEnv, eventType: string) {
    // Get the config for the component. config will have the behaviours and actions.
    const componentConfig = component.getComponentConfig();
    const behaviours = componentConfig.behaviours || [];
    // For each of the behaviours, get the handlers for the actions authored and execute them.
    for (let index = 0; index < behaviours.length; index += 1) {
      const { behaviour, actions } = behaviours[index];
      if (actions) {
        const actionHandlers = this.getActionHandlers(actions, behaviour, eventType);
        if (actionHandlers && actionHandlers.length > 0) {
          this.#executeActions(actionHandlers, component, eventType);
        }
      }
    }
  }

  /**
   * Handles the change event triggerred by component.
   * Incase of React, every keystoke it is being called.
   * Going to dispatch the event to store to handle the update
   * of the values.
   *
   * Also clear the error messages of that field component by calling clearMessages on messageManager
   *
   * @param component this c11nEnv object
   * @param event DOM Event object
   */
  // eslint-disable-next-line sonarjs/cognitive-complexity
  static changeHandler = (component: C11nEnv, event: Event | BrowserEvent) => {
    const fieldName = component.getStateProps().value;
    const context = component.getContextName();

    clearMessages({
      type: publicConstants.MESSAGES.MESSAGES_TYPE_ERROR,
      property: fieldName,
      pageReference: component.getPageReference(),
      context
    });

    // ((event.value !== null) && (event.prop === undefined)) - Events with value but no prop may be seen from some
    //  non-React environments that access Core but treated the same as Event and nativeEvent
    const isBrowserEvent =
      EnvironmentInfo.getRuntime() === 'Browser'
        ? (event as { nativeEvent?: Event })?.nativeEvent instanceof Event || event instanceof Event
        : !!(event as { nativeEvent?: Event })?.nativeEvent;

    if (
      isBrowserEvent ||
      ((event as EventWithPossibleValue).value !== null && (event as EventWithPossibleValue).prop === undefined)
    ) {
      let { target } = event;

      // auxiliaryTarget is set if the event object is manually created as an instance of Event class
      // because the actual "target" object is readOnly - Ex. in Date, Time, DateTime components
      if (target === null && event && (event as BrowserEvent).auxiliaryTarget) {
        target = (event as BrowserEvent).auxiliaryTarget;
      }

      let value: unknown;
      if (target === undefined) {
        value = (event as BrowserEvent).value;
      } else if ('value' in target) {
        value = target.value;
      } else {
        value = target.innerText;
      }

      const fullRef = this.getFullRef(component);
      dispatch({
        type: SET_PROPERTY,
        payload: {
          reference: fullRef,
          value,
          context
        }
      });

      /**
       * In case of editable controls, this should happen in blur
       * except for input like select
       */
      if (target === undefined || !this.isEditable(target) || this.isNonKeyInput(target)) {
        dispatch({
          type: FIELD_CHANGE,
          payload: {
            reference: fullRef,
            pageReference: component.getPageReference(),
            value,
            context,
            propertyName: component.getComponentConfig().value,
            classID: component.getValue(Constants.CLASS_ID)
          }
        });
      }
    } else {
      const pageReference = component.getPageReference();
      const props = component.getStateProps();
      for (const key of Object.keys(event)) {
        const prop = props[key];
        const value = (event as { [key: string]: any })[key];
        dispatch({
          type: SET_PROPERTY,
          payload: {
            reference: ContextProcessor.getContextPath(pageReference, prop),
            value,
            context
          }
        });

        // FIXME: This will cause field change on all changes for non event type
        // actions. This needs to be thought about since components are not passing
        // the event to the handlers...
        dispatch({
          type: FIELD_CHANGE,
          payload: {
            reference: ContextProcessor.getContextPath(pageReference, prop),
            value,
            context
          }
        });
      }
    }
  };

  /**
   * handles the blur event, and triggers FIELD_CHANGE event
   * so that ChangeObserver can listen for it.
   * In case e is Object (not event), then it will treat as
   * explicit event trigger by infra to handle complex values.
   * Like more than one property eg., DataRange Control, Autocomplete etc.,
   * @param c11nEnv DOM Event object
   * @param event Property need to be updated eq., .FirstName
   */
  static blurHandler = (c11nEnv: C11nEnv, event: Event | (BrowserEvent & EventWithPossibleAuxTarget)) => {
    let { target } = event;

    // auxiliaryTarget is set if the event object is manually created as an instance of Event class
    // because the actual "target" object is readOnly - Ex. in Date, Time, DateTime components
    if (target === null && event && 'auxiliaryTarget' in event) {
      target = event.auxiliaryTarget;
    }
    const { value, validationValue } = target;
    const fullRef = this.getFullRef(c11nEnv);
    const context = c11nEnv.getContextName();

    // clear error messages if target is being sent with a flag to clear them - Ex. in Date, Time, DateTime components
    if (target.clearMessages) {
      clearMessages({
        type: publicConstants.MESSAGES.MESSAGES_TYPE_ERROR,
        property: c11nEnv.getStateProps().value,
        pageReference: c11nEnv.getPageReference(),
        context
      });
    }

    if (this.isEditable(target)) {
      c11nEnv.getValidationApi().validate('validationValue' in target ? validationValue : value);
      if (c11nEnv.isInsideList()) {
        c11nEnv.getListActions().updateProperty(c11nEnv.getStateProps().value, value);
      }
      // prevent for nonKeyInputs as they already dispatch FIELD_CHANGE in changeHandler
      if (!this.isNonKeyInput(target)) {
        dispatch({
          type: FIELD_CHANGE,
          payload: {
            reference: fullRef,
            pageReference: c11nEnv.getPageReference(),
            value,
            context,
            propertyName: c11nEnv.getComponentConfig().value,
            classID: c11nEnv.getValue(Constants.CLASS_ID)
          }
        });
      }
    }
  };

  /**
   * handles the changes and blur events
   * @param component c11n_env object
   * @param event target event
   */
  static eventHandler = (component: C11nEnv, event: Event) => {
    const { type } = event;
    switch (type) {
      case 'change':
        this.changeHandler(component, event);
        break;
      case 'blur':
        this.blurHandler(component, event);
        break;
      default:
        break;
    }
    if (type) {
      this.#handleBehaviours(component, type);
    }
  };

  /**
   * pauses the queue
   */
  static pause = () => {
    this.#paused = true;
  };

  /**
   * resumes the queue processing
   */
  static resume = () => {
    this.#paused = false;
    this.#executeNextAction();
  };

  /**
   * checks if the queue is empty or not
   * @returns boolean
   */
  static isQueueEmpty = (): boolean => this.#actionsQueue.length === 0;

  /**
   * clears queue items
   */
  static clearQueue = () => {
    this.#actionsQueue = [];
  };

  /**
   * returns the action queue
   */
  static getActionsQueue = () => {
    return this.#actionsQueue;
  };

  static EVENTS_MAP = {
    click: 'onClick',
    change: 'onChange',
    hover: 'onMouseEnter',
    blur: 'onBlur'
  };
}

export default Processor;
